import { useState, useEffect } from 'preact/hooks';
import { superAdminFetch, superAdminPost, superAdminPut } from '../../lib/super-admin-api';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.unplug-app.com';

export function SuperAdminWorkspaceDetail({ id }) {
  const [workspace, setWorkspace] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Form states
  const [extendDays, setExtendDays] = useState(7);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountReason, setDiscountReason] = useState('');
  const [notes, setNotes] = useState('');
  const [billingMethod, setBillingMethod] = useState('stripe');
  const [invoiceDueDays, setInvoiceDueDays] = useState(30);

  // Billing details form state
  const [billingDetails, setBillingDetails] = useState({
    customer_type: 'business',
    company_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postal_code: '',
    country_code: 'MT',
    vat_number: '',
    vat_valid: false,
    business_registration: '',
    contact_email: '',
    contact_name: '',
    xero_contact_id: '',
    billing_start_date: ''
  });
  const [vatValidating, setVatValidating] = useState(false);
  const [vatValidationResult, setVatValidationResult] = useState(null);
  const [xeroSearchTerm, setXeroSearchTerm] = useState('');
  const [xeroContacts, setXeroContacts] = useState([]);
  const [xeroSearching, setXeroSearching] = useState(false);
  const [billNowLoading, setBillNowLoading] = useState(false);
  const [lastBillNowResult, setLastBillNowResult] = useState(null);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationResult, setNotificationResult] = useState(null);

  useEffect(() => {
    fetchWorkspace();
    fetchEvents();
    fetchBillingDetails();
  }, [id]);

  async function fetchWorkspace() {
    try {
      const res = await superAdminFetch(`${API_URL}/api/super-admin/workspaces/${id}`);
      if (!res.ok) throw new Error('Failed to fetch workspace');

      const data = await res.json();
      setWorkspace(data);
      setNotes(data.notes || '');
      setBillingMethod(data.subscription?.billing_method || 'stripe');
      setInvoiceDueDays(data.subscription?.invoice_due_days || 30);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchEvents() {
    try {
      const res = await superAdminFetch(`${API_URL}/api/super-admin/events?workspace_id=${id}&limit=20`);
      if (res.ok) {
        const data = await res.json();
        setEvents(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch events:', err);
    }
  }

  async function handleExtendTrial() {
    if (!extendDays || extendDays < 1) return;

    setActionLoading(true);
    try {
      const res = await superAdminPost(`${API_URL}/api/super-admin/workspaces/${id}/extend-trial`, { days: extendDays });

      if (!res.ok) throw new Error('Failed to extend trial');

      await fetchWorkspace();
      await fetchEvents();
      setExtendDays(7);
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleActivate() {
    if (!confirm('Manually activate this subscription for 12 months?')) return;

    setActionLoading(true);
    try {
      const res = await superAdminPost(`${API_URL}/api/super-admin/workspaces/${id}/activate`, { months: 12 });

      if (!res.ok) throw new Error('Failed to activate');

      await fetchWorkspace();
      await fetchEvents();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleCancel() {
    if (!confirm('Are you sure you want to cancel this subscription?')) return;

    setActionLoading(true);
    try {
      const res = await superAdminPost(`${API_URL}/api/super-admin/workspaces/${id}/cancel`, { reason: 'Cancelled by super admin' });

      if (!res.ok) throw new Error('Failed to cancel');

      await fetchWorkspace();
      await fetchEvents();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleApplyDiscount() {
    if (discountPercent < 0 || discountPercent > 100) {
      alert('Discount must be between 0 and 100%');
      return;
    }

    setActionLoading(true);
    try {
      const res = await superAdminPost(`${API_URL}/api/super-admin/workspaces/${id}/discount`, {
        percent: discountPercent,
        reason: discountReason
      });

      if (!res.ok) throw new Error('Failed to apply discount');

      await fetchWorkspace();
      await fetchEvents();
      setDiscountPercent(0);
      setDiscountReason('');
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleUpdateNotes() {
    setActionLoading(true);
    try {
      const res = await superAdminPut(`${API_URL}/api/super-admin/workspaces/${id}/subscription`, { notes });

      if (!res.ok) throw new Error('Failed to update notes');

      await fetchWorkspace();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleUpdateBillingMethod() {
    setActionLoading(true);
    try {
      const res = await superAdminPut(`${API_URL}/api/super-admin/workspaces/${id}/subscription`, {
        billing_method: billingMethod,
        invoice_due_days: invoiceDueDays
      });

      if (!res.ok) throw new Error('Failed to update billing method');

      await fetchWorkspace();
      await fetchEvents();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function fetchBillingDetails() {
    try {
      const res = await superAdminFetch(`${API_URL}/api/super-admin/workspaces/${id}/billing`);
      if (res.ok) {
        const data = await res.json();
        if (data.billing) {
          setBillingDetails({
            customer_type: data.billing.customer_type || 'business',
            company_name: data.billing.company_name || '',
            address_line1: data.billing.address_line1 || '',
            address_line2: data.billing.address_line2 || '',
            city: data.billing.city || '',
            postal_code: data.billing.postal_code || '',
            country_code: data.billing.country_code || 'MT',
            vat_number: data.billing.vat_number || '',
            vat_valid: data.billing.vat_valid === 1,
            business_registration: data.billing.business_registration || '',
            contact_email: data.billing.contact_email || '',
            contact_name: data.billing.contact_name || '',
            xero_contact_id: data.billing.xero_contact_id || '',
            billing_start_date: data.billing_start_date || ''
          });
        }
      }
    } catch (err) {
      console.error('Failed to fetch billing details:', err);
    }
  }

  async function handleValidateVAT() {
    if (!billingDetails.vat_number) {
      alert('Please enter a VAT number');
      return;
    }

    setVatValidating(true);
    setVatValidationResult(null);
    try {
      const res = await superAdminPost(`${API_URL}/api/super-admin/workspaces/${id}/validate-vat`, {
        vat_number: billingDetails.vat_number
      });

      const data = await res.json();

      // Check for HTTP errors
      if (!res.ok) {
        setVatValidationResult({ valid: false, error: data.error || `HTTP ${res.status}` });
        return;
      }

      setVatValidationResult(data);

      if (data.valid) {
        // Auto-fill company name if returned from VIES
        setBillingDetails(prev => ({
          ...prev,
          vat_valid: true,
          company_name: data.company_name || prev.company_name,
          country_code: data.country_code || prev.country_code
        }));
      } else {
        setBillingDetails(prev => ({ ...prev, vat_valid: false }));
      }
    } catch (err) {
      console.error('VAT validation error:', err);
      setVatValidationResult({ valid: false, error: `Network error: ${err.message}` });
    } finally {
      setVatValidating(false);
    }
  }

  async function handleSearchXeroContacts() {
    if (!xeroSearchTerm || xeroSearchTerm.length < 2) {
      alert('Enter at least 2 characters to search');
      return;
    }

    setXeroSearching(true);
    try {
      const res = await superAdminFetch(
        `${API_URL}/api/super-admin/xero/contacts?search=${encodeURIComponent(xeroSearchTerm)}`
      );
      const data = await res.json();
      if (data.error) {
        alert(`Xero search failed: ${data.error}`);
        setXeroContacts([]);
      } else {
        setXeroContacts(data.contacts || []);
        if (data.contacts?.length === 0) {
          alert('No contacts found matching that name');
        }
      }
    } catch (err) {
      console.error('Failed to search Xero contacts:', err);
      alert('Failed to search Xero contacts');
    } finally {
      setXeroSearching(false);
    }
  }

  async function handleSaveBillingDetails() {
    setActionLoading(true);
    try {
      const res = await superAdminPut(`${API_URL}/api/super-admin/workspaces/${id}/billing`, billingDetails);

      if (!res.ok) throw new Error('Failed to save billing details');

      // Refresh all data - workspace contains subscription fields that may have been updated
      await fetchWorkspace();
      await fetchBillingDetails();
      await fetchEvents();
      alert('Billing details saved successfully');
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleBillNow() {
    if (!confirm('This will:\n\n1. Create a Xero invoice for this month\n2. Activate the subscription\n3. Set the billing start date to today\n4. Schedule next billing for one month from now\n\nContinue?')) {
      return;
    }

    setBillNowLoading(true);
    setLastBillNowResult(null);
    try {
      const res = await superAdminPost(`${API_URL}/api/super-admin/workspaces/${id}/bill-now`, {});

      const data = await res.json();

      if (!res.ok) {
        // Prefer details (specific error) over generic error message
        throw new Error(data.details || data.error || 'Failed to create invoice');
      }

      setLastBillNowResult(data);
      await fetchWorkspace();
      await fetchBillingDetails();
      await fetchEvents();

      alert(`Invoice created successfully!\n\nInvoice: ${data.invoice_number}\nTotal: €${data.total.toFixed(2)}\nNext billing: ${data.next_billing_date}`);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setBillNowLoading(false);
    }
  }

  async function handleTriggerNotification(type) {
    const typeNames = {
      daily_summary: 'Daily Summary',
      weekly_summary: 'Weekly Summary',
      weather_forecast: 'Weather Forecast'
    };

    if (!confirm(`Send ${typeNames[type]} notification now?\n\nThis will send immediately, bypassing the scheduled time.`)) {
      return;
    }

    setNotificationLoading(true);
    setNotificationResult(null);
    try {
      const res = await superAdminPost(`${API_URL}/api/super-admin/trigger-notifications`, {
        workspace_id: id,
        type
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || data.error || 'Failed to trigger notification');
      }

      setNotificationResult({
        success: true,
        type,
        ...data.result
      });
      alert(`${typeNames[type]} notification sent successfully!`);
    } catch (err) {
      setNotificationResult({
        success: false,
        error: err.message
      });
      alert(`Error: ${err.message}`);
    } finally {
      setNotificationLoading(false);
    }
  }

  function updateBillingField(field, value) {
    setBillingDetails(prev => ({ ...prev, [field]: value }));
  }

  if (loading) {
    return (
      <div class="super-admin-page">
        <div class="loading-state">
          <div class="spinner" />
          <p>Loading workspace...</p>
        </div>
      </div>
    );
  }

  if (error || !workspace) {
    return (
      <div class="super-admin-page">
        <div class="error-state">
          <h2>Error</h2>
          <p>{error || 'Workspace not found'}</p>
          <a href="/super-admin/workspaces" class="btn btn-primary">Back to Workspaces</a>
        </div>
      </div>
    );
  }

  const sub = workspace.subscription;

  return (
    <div class="super-admin-page">
      <div class="page-header">
        <div>
          <a href="/super-admin/workspaces" class="text-muted">&larr; Back to Workspaces</a>
          <h1 class="mt-2">
            {workspace.slack_team_icon && (
              <img src={workspace.slack_team_icon} alt="" class="workspace-icon-lg" />
            )}
            {workspace.name}
          </h1>
          <p class="text-muted">{workspace.id}</p>
        </div>
      </div>

      <div class="grid-2">
        {/* Workspace Info */}
        <div class="card">
          <div class="card-header">
            <h2>Workspace Info</h2>
          </div>
          <div class="card-body">
            <dl class="info-list">
              <dt>Domain</dt>
              <dd>{workspace.domain || '-'}</dd>

              <dt>Created</dt>
              <dd>{formatDate(workspace.created_at)}</dd>

              <dt>Users</dt>
              <dd>
                {workspace.user_count || 0}
                {sub?.seats_limit && <span class="text-muted"> / {sub.seats_limit} limit</span>}
              </dd>

              <dt>Active Users</dt>
              <dd>{workspace.active_users || 0}</dd>

              <dt>Leave Requests</dt>
              <dd>{workspace.leave_count || 0}</dd>
            </dl>
          </div>
        </div>

        {/* Subscription Info */}
        <div class="card">
          <div class="card-header">
            <h2>Subscription</h2>
            <span class={`badge badge-${getStatusColor(sub?.status)}`}>
              {sub?.status || 'none'}
            </span>
          </div>
          <div class="card-body">
            <dl class="info-list">
              <dt>Plan</dt>
              <dd>{sub?.plan || 'none'}</dd>

              <dt>Trial Started</dt>
              <dd>{formatDate(sub?.trial_started_at)}</dd>

              <dt>Trial Ends</dt>
              <dd>
                {sub?.trial_ends_at ? (
                  <>
                    {formatDate(sub.trial_ends_at)}
                    <span class="text-muted"> ({getDaysRemaining(sub.trial_ends_at)} days)</span>
                  </>
                ) : '-'}
              </dd>

              <dt>Subscription Started</dt>
              <dd>{formatDate(sub?.subscription_started_at)}</dd>

              <dt>Subscription Ends</dt>
              <dd>{formatDate(sub?.subscription_ends_at)}</dd>

              <dt>Billing Email</dt>
              <dd>{sub?.billing_email || '-'}</dd>

              <dt>Stripe Customer</dt>
              <dd>{sub?.stripe_customer_id || '-'}</dd>

              <dt>Discount</dt>
              <dd>
                {sub?.discount_percent > 0 ? (
                  <>
                    <span class="badge badge-info">{sub.discount_percent}%</span>
                    {sub.discount_reason && (
                      <span class="text-muted"> - {sub.discount_reason}</span>
                    )}
                  </>
                ) : 'None'}
              </dd>

              <dt>Billing Method</dt>
              <dd>
                <span class={`badge badge-${sub?.billing_method === 'invoice' ? 'warning' : 'info'}`}>
                  {sub?.billing_method === 'invoice' ? 'Invoice Only' : 'Stripe'}
                </span>
                {sub?.billing_method === 'invoice' && (
                  <span class="text-muted"> - {sub?.invoice_due_days || 30} days terms</span>
                )}
              </dd>
            </dl>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div class="card mt-4">
        <div class="card-header">
          <h2>Actions</h2>
        </div>
        <div class="card-body">
          <div class="action-sections">
            {/* Extend Trial */}
            {(sub?.status === 'trialing' || sub?.status === 'expired') && (
              <div class="action-section">
                <h3>Extend Trial</h3>
                <div class="inline-form">
                  <input
                    type="number"
                    min="1"
                    max="90"
                    value={extendDays}
                    onChange={e => setExtendDays(parseInt(e.target.value))}
                    class="input-sm"
                  />
                  <span>days</span>
                  <button
                    class="btn btn-primary"
                    onClick={handleExtendTrial}
                    disabled={actionLoading}
                  >
                    Extend
                  </button>
                </div>
              </div>
            )}

            {/* Activate */}
            {sub?.status !== 'active' && (
              <div class="action-section">
                <h3>Manual Activation</h3>
                <p class="text-muted">Manually activate subscription for 12 months</p>
                <button
                  class="btn btn-success"
                  onClick={handleActivate}
                  disabled={actionLoading}
                >
                  Activate Subscription
                </button>
              </div>
            )}

            {/* Apply Discount */}
            <div class="action-section">
              <h3>Apply Discount</h3>
              <div class="inline-form">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={discountPercent}
                  onChange={e => setDiscountPercent(parseInt(e.target.value))}
                  class="input-sm"
                  placeholder="0"
                />
                <span>%</span>
                <input
                  type="text"
                  value={discountReason}
                  onChange={e => setDiscountReason(e.target.value)}
                  placeholder="Reason (optional)"
                  class="input-md"
                />
                <button
                  class="btn btn-primary"
                  onClick={handleApplyDiscount}
                  disabled={actionLoading}
                >
                  Apply
                </button>
              </div>
            </div>

            {/* Cancel */}
            {sub?.status === 'active' && (
              <div class="action-section">
                <h3>Cancel Subscription</h3>
                <p class="text-muted">This will immediately cancel the subscription</p>
                <button
                  class="btn btn-danger"
                  onClick={handleCancel}
                  disabled={actionLoading}
                >
                  Cancel Subscription
                </button>
              </div>
            )}

            {/* Bill Now - for invoice-only customers who haven't been billed yet */}
            {sub?.billing_method === 'invoice' && (
              <div class="action-section" style={{ borderTop: '2px solid var(--color-success)', paddingTop: '1.5rem', marginTop: '1.5rem' }}>
                <h3>Generate Invoice</h3>
                <p class="text-muted">
                  {sub?.next_billing_date
                    ? `Next invoice scheduled for ${new Date(sub.next_billing_date).toLocaleDateString()}. Use this to generate an invoice immediately.`
                    : 'Create first invoice immediately. Billing will recur monthly on the same day.'}
                </p>
                {!billingDetails.company_name && !billingDetails.xero_contact_id ? (
                  <p class="text-warning">Please add billing details or link a Xero contact before billing.</p>
                ) : (
                  <button
                    class="btn btn-success"
                    onClick={handleBillNow}
                    disabled={billNowLoading || actionLoading}
                  >
                    {billNowLoading ? 'Creating Invoice...' : (sub?.status === 'active' ? 'Generate Invoice Now' : 'Bill Now & Activate')}
                  </button>
                )}
                {sub?.next_billing_date && (
                  <p class="text-muted mt-2" style={{ fontSize: '0.85rem' }}>
                    Next billing: <strong>{sub.next_billing_date}</strong>
                  </p>
                )}
                {lastBillNowResult && (
                  <div class="mt-3" style={{ background: 'var(--color-background-alt)', padding: '1rem', borderRadius: '4px' }}>
                    <strong>Invoice Created:</strong>
                    <dl class="info-list mt-2">
                      <dt>Invoice Number</dt>
                      <dd>{lastBillNowResult.invoice_number}</dd>
                      {lastBillNowResult.discount_percent > 0 && (
                        <>
                          <dt>Base Price</dt>
                          <dd>€{lastBillNowResult.base_price?.toFixed(2)}</dd>
                          <dt>Discount ({lastBillNowResult.discount_percent}%)</dt>
                          <dd>-€{lastBillNowResult.discount_amount?.toFixed(2)}</dd>
                        </>
                      )}
                      <dt>Subtotal</dt>
                      <dd>€{lastBillNowResult.subtotal?.toFixed(2)}</dd>
                      <dt>VAT ({lastBillNowResult.vat_rate}%)</dt>
                      <dd>€{lastBillNowResult.vat_amount?.toFixed(2)}</dd>
                      <dt>Total</dt>
                      <dd><strong>€{lastBillNowResult.total?.toFixed(2)}</strong></dd>
                      <dt>Next Billing</dt>
                      <dd>{lastBillNowResult.next_billing_date}</dd>
                    </dl>
                  </div>
                )}
              </div>
            )}

            {/* Test Notifications */}
            <div class="action-section" style={{ borderTop: '2px solid var(--color-info)', paddingTop: '1.5rem', marginTop: '1.5rem' }}>
              <h3>Test Notifications</h3>
              <p class="text-muted">Manually trigger notifications (bypasses scheduled time)</p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  class="btn btn-info"
                  onClick={() => handleTriggerNotification('daily_summary')}
                  disabled={notificationLoading}
                >
                  {notificationLoading ? 'Sending...' : '📅 Daily Summary'}
                </button>
                <button
                  class="btn btn-info"
                  onClick={() => handleTriggerNotification('weekly_summary')}
                  disabled={notificationLoading}
                >
                  {notificationLoading ? 'Sending...' : '📊 Weekly Summary'}
                </button>
                <button
                  class="btn btn-info"
                  onClick={() => handleTriggerNotification('weather_forecast')}
                  disabled={notificationLoading}
                >
                  {notificationLoading ? 'Sending...' : '🌤️ Weather Forecast'}
                </button>
              </div>
              {notificationResult && (
                <div class={`mt-3 ${notificationResult.success ? 'text-success' : 'text-danger'}`}>
                  {notificationResult.success ? (
                    <span>✓ Notification sent! Check #{notificationResult.results?.[0]?.channel || 'configured channel'}</span>
                  ) : (
                    <span>✗ {notificationResult.error}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Billing Method */}
      <div class="card mt-4">
        <div class="card-header">
          <h2>Billing Method</h2>
          <span class={`badge badge-${billingMethod === 'invoice' ? 'warning' : 'info'}`}>
            {billingMethod === 'invoice' ? 'Invoice Only' : 'Stripe'}
          </span>
        </div>
        <div class="card-body">
          <p class="text-muted mb-3">
            Invoice-only customers receive Xero invoices without Stripe charges. Useful for local Maltese companies who prefer traditional invoicing.
          </p>
          <div class="action-section">
            <div class="inline-form" style={{ flexWrap: 'wrap', gap: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="radio"
                  name="billingMethod"
                  value="stripe"
                  checked={billingMethod === 'stripe'}
                  onChange={e => setBillingMethod(e.target.value)}
                />
                <span>Stripe (online payments)</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="radio"
                  name="billingMethod"
                  value="invoice"
                  checked={billingMethod === 'invoice'}
                  onChange={e => setBillingMethod(e.target.value)}
                />
                <span>Invoice Only (Xero)</span>
              </label>
            </div>

            {billingMethod === 'invoice' && (
              <div class="inline-form mt-3">
                <label>Invoice due in</label>
                <input
                  type="number"
                  min="7"
                  max="90"
                  value={invoiceDueDays}
                  onChange={e => setInvoiceDueDays(parseInt(e.target.value))}
                  class="input-sm"
                  style={{ width: '80px' }}
                />
                <span>days</span>
              </div>
            )}

            <button
              class="btn btn-primary mt-3"
              onClick={handleUpdateBillingMethod}
              disabled={actionLoading}
            >
              Save Billing Method
            </button>
          </div>
        </div>
      </div>

      {/* Billing Details */}
      <div class="card mt-4">
        <div class="card-header">
          <h2>Billing Details</h2>
        </div>
        <div class="card-body">
          <div style={{ maxWidth: '800px' }}>
            {/* Customer Type */}
            <div class="form-group">
              <label>Customer Type</label>
              <div class="inline-form" style={{ gap: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="radio"
                    name="customerType"
                    value="business"
                    checked={billingDetails.customer_type === 'business'}
                    onChange={e => updateBillingField('customer_type', e.target.value)}
                  />
                  Business
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="radio"
                    name="customerType"
                    value="individual"
                    checked={billingDetails.customer_type === 'individual'}
                    onChange={e => updateBillingField('customer_type', e.target.value)}
                  />
                  Individual
                </label>
              </div>
            </div>

            {/* Company/Contact Name */}
            <div class="form-row">
              <div class="form-group">
                <label>{billingDetails.customer_type === 'business' ? 'Company Name' : 'Full Name'}</label>
                <input
                  type="text"
                  value={billingDetails.company_name}
                  onChange={e => updateBillingField('company_name', e.target.value)}
                  placeholder={billingDetails.customer_type === 'business' ? 'ACME Ltd' : 'John Smith'}
                  class="input-full"
                />
              </div>
              <div class="form-group">
                <label>Contact Name</label>
                <input
                  type="text"
                  value={billingDetails.contact_name}
                  onChange={e => updateBillingField('contact_name', e.target.value)}
                  placeholder="Accounts contact"
                  class="input-full"
                />
              </div>
            </div>

            {/* Address */}
            <div class="form-group">
              <label>Address Line 1</label>
              <input
                type="text"
                value={billingDetails.address_line1}
                onChange={e => updateBillingField('address_line1', e.target.value)}
                placeholder="Street address"
                class="input-full"
              />
            </div>
            <div class="form-group">
              <label>Address Line 2</label>
              <input
                type="text"
                value={billingDetails.address_line2}
                onChange={e => updateBillingField('address_line2', e.target.value)}
                placeholder="Apartment, suite, etc."
                class="input-full"
              />
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={billingDetails.city}
                  onChange={e => updateBillingField('city', e.target.value)}
                  placeholder="City"
                  class="input-full"
                />
              </div>
              <div class="form-group">
                <label>Postal Code</label>
                <input
                  type="text"
                  value={billingDetails.postal_code}
                  onChange={e => updateBillingField('postal_code', e.target.value)}
                  placeholder="Postal code"
                  class="input-full"
                />
              </div>
              <div class="form-group">
                <label>Country</label>
                <select
                  value={billingDetails.country_code}
                  onChange={e => updateBillingField('country_code', e.target.value)}
                  class="input-full"
                >
                  <option value="MT">Malta</option>
                  <option value="AT">Austria</option>
                  <option value="BE">Belgium</option>
                  <option value="BG">Bulgaria</option>
                  <option value="HR">Croatia</option>
                  <option value="CY">Cyprus</option>
                  <option value="CZ">Czech Republic</option>
                  <option value="DK">Denmark</option>
                  <option value="EE">Estonia</option>
                  <option value="FI">Finland</option>
                  <option value="FR">France</option>
                  <option value="DE">Germany</option>
                  <option value="GR">Greece</option>
                  <option value="HU">Hungary</option>
                  <option value="IE">Ireland</option>
                  <option value="IT">Italy</option>
                  <option value="LV">Latvia</option>
                  <option value="LT">Lithuania</option>
                  <option value="LU">Luxembourg</option>
                  <option value="NL">Netherlands</option>
                  <option value="PL">Poland</option>
                  <option value="PT">Portugal</option>
                  <option value="RO">Romania</option>
                  <option value="SK">Slovakia</option>
                  <option value="SI">Slovenia</option>
                  <option value="ES">Spain</option>
                  <option value="SE">Sweden</option>
                  <option value="GB">United Kingdom</option>
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="AU">Australia</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
            </div>

            {/* Contact Email */}
            <div class="form-group">
              <label>Billing Email</label>
              <input
                type="email"
                value={billingDetails.contact_email}
                onChange={e => updateBillingField('contact_email', e.target.value)}
                placeholder="accounts@company.com"
                class="input-full"
              />
            </div>

            {/* VAT Number (for EU businesses) */}
            {billingDetails.customer_type === 'business' && (
              <div class="form-group">
                <label>VAT Number (EU)</label>
                <div class="inline-form">
                  <input
                    type="text"
                    value={billingDetails.vat_number}
                    onChange={e => updateBillingField('vat_number', e.target.value)}
                    placeholder="MT12345678"
                    class="input-md"
                  />
                  <button
                    class="btn btn-secondary"
                    onClick={handleValidateVAT}
                    disabled={vatValidating || !billingDetails.vat_number}
                  >
                    {vatValidating ? 'Validating...' : 'Validate VAT'}
                  </button>
                  {billingDetails.vat_valid && (
                    <span class="badge badge-success">Valid</span>
                  )}
                </div>
                {vatValidationResult && (
                  <div class={`mt-2 ${vatValidationResult.valid ? 'text-success' : 'text-danger'}`}>
                    {vatValidationResult.valid ? (
                      <>
                        VAT valid - {vatValidationResult.company_name}
                        {vatValidationResult.company_address && (
                          <span class="text-muted"> ({vatValidationResult.company_address})</span>
                        )}
                      </>
                    ) : (
                      <>VAT invalid: {vatValidationResult.error || 'Not found in VIES'}</>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Business Registration (for non-EU businesses) */}
            {billingDetails.customer_type === 'business' && !['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'].includes(billingDetails.country_code) && (
              <div class="form-group">
                <label>Business Registration Number</label>
                <input
                  type="text"
                  value={billingDetails.business_registration}
                  onChange={e => updateBillingField('business_registration', e.target.value)}
                  placeholder="Company registration number"
                  class="input-full"
                />
                <small class="text-muted">Required for non-EU B2B invoices</small>
              </div>
            )}

            {/* Xero Contact ID */}
            <div class="form-group">
              <label>Xero Contact ID</label>
              <input
                type="text"
                value={billingDetails.xero_contact_id}
                onChange={e => updateBillingField('xero_contact_id', e.target.value)}
                placeholder="Leave blank to auto-create"
                class="input-full"
              />
              <small class="text-muted">If this customer already exists in Xero, enter their Contact ID here</small>

              <div class="inline-form mt-2">
                <input
                  type="text"
                  value={xeroSearchTerm}
                  onChange={e => setXeroSearchTerm(e.target.value)}
                  placeholder="Search Xero contacts..."
                  class="input-md"
                />
                <button
                  class="btn btn-secondary"
                  onClick={handleSearchXeroContacts}
                  disabled={xeroSearching || xeroSearchTerm.length < 2}
                >
                  {xeroSearching ? 'Searching...' : 'Search Xero'}
                </button>
              </div>

              {xeroContacts.length > 0 && (
                <div class="xero-contacts-list mt-2" style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: '4px' }}>
                  {xeroContacts.map(contact => (
                    <div
                      key={contact.id}
                      class="xero-contact-item"
                      onClick={() => {
                        // Auto-populate form with Xero contact details
                        setBillingDetails(prev => ({
                          ...prev,
                          xero_contact_id: contact.id,
                          company_name: contact.name || prev.company_name,
                          contact_email: contact.email || prev.contact_email,
                          contact_name: contact.firstName && contact.lastName
                            ? `${contact.firstName} ${contact.lastName}`
                            : prev.contact_name,
                          address_line1: contact.addressLine1 || prev.address_line1,
                          address_line2: contact.addressLine2 || prev.address_line2,
                          city: contact.city || prev.city,
                          postal_code: contact.postalCode || prev.postal_code,
                          vat_number: contact.taxNumber || prev.vat_number,
                          // Try to map country name to code
                          country_code: mapCountryToCode(contact.country) || prev.country_code
                        }));
                        setXeroContacts([]);
                        setXeroSearchTerm('');
                      }}
                      style={{
                        padding: '0.75rem',
                        cursor: 'pointer',
                        borderBottom: '1px solid var(--color-border)',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--color-background-alt)'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <strong>{contact.name}</strong>
                      {contact.email && <span class="text-muted"> - {contact.email}</span>}
                      {contact.city && <span class="text-muted"> ({contact.city})</span>}
                      {contact.taxNumber && <span class="badge badge-info ml-2" style={{ marginLeft: '0.5rem' }}>VAT: {contact.taxNumber}</span>}
                      <br />
                      <small class="text-muted">{contact.id}</small>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Billing Start Date */}
            <div class="form-group">
              <label>Billing Start Date (Override)</label>
              <input
                type="date"
                value={billingDetails.billing_start_date}
                onChange={e => updateBillingField('billing_start_date', e.target.value)}
                class="input-md"
              />
              <small class="text-muted">Override when billing starts for this workspace. Leave blank to use subscription start date.</small>
            </div>

            <button
              class="btn btn-primary mt-3"
              onClick={handleSaveBillingDetails}
              disabled={actionLoading}
            >
              Save Billing Details
            </button>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div class="card mt-4">
        <div class="card-header">
          <h2>Admin Notes</h2>
        </div>
        <div class="card-body">
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Add internal notes about this workspace..."
            rows="3"
            class="textarea-full"
          />
          <button
            class="btn btn-primary mt-2"
            onClick={handleUpdateNotes}
            disabled={actionLoading}
          >
            Save Notes
          </button>
        </div>
      </div>

      {/* Recent Events */}
      <div class="card mt-4">
        <div class="card-header">
          <h2>Recent Events</h2>
        </div>
        <div class="card-body">
          {events.length > 0 ? (
            <table class="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Event</th>
                  <th>Status Change</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {events.map(evt => (
                  <tr key={evt.id}>
                    <td>{formatDateTime(evt.created_at)}</td>
                    <td>
                      <span class={`badge badge-${getEventColor(evt.event_type)}`}>
                        {evt.event_type}
                      </span>
                    </td>
                    <td>
                      {evt.old_status && evt.new_status ? (
                        <>
                          <span class="text-muted">{evt.old_status}</span>
                          {' → '}
                          <span>{evt.new_status}</span>
                        </>
                      ) : '-'}
                    </td>
                    <td>{evt.created_by || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p class="text-muted">No events recorded yet</p>
          )}
        </div>
      </div>
    </div>
  );
}

function getStatusColor(status) {
  switch (status) {
    case 'active': return 'success';
    case 'trialing': return 'warning';
    case 'expired':
    case 'cancelled': return 'danger';
    case 'suspended': return 'danger';
    default: return 'secondary';
  }
}

function getEventColor(eventType) {
  if (eventType.includes('activated') || eventType.includes('created')) return 'success';
  if (eventType.includes('extended') || eventType.includes('updated')) return 'info';
  if (eventType.includes('cancelled') || eventType.includes('expired')) return 'danger';
  return 'secondary';
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function getDaysRemaining(dateStr) {
  if (!dateStr) return 0;
  const endDate = new Date(dateStr);
  const now = new Date();
  return Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
}

// Map country names to ISO codes
function mapCountryToCode(countryName) {
  if (!countryName) return null;
  const name = countryName.toLowerCase().trim();
  const map = {
    'malta': 'MT',
    'austria': 'AT',
    'belgium': 'BE',
    'bulgaria': 'BG',
    'croatia': 'HR',
    'cyprus': 'CY',
    'czech republic': 'CZ',
    'czechia': 'CZ',
    'denmark': 'DK',
    'estonia': 'EE',
    'finland': 'FI',
    'france': 'FR',
    'germany': 'DE',
    'greece': 'GR',
    'hungary': 'HU',
    'ireland': 'IE',
    'italy': 'IT',
    'latvia': 'LV',
    'lithuania': 'LT',
    'luxembourg': 'LU',
    'netherlands': 'NL',
    'poland': 'PL',
    'portugal': 'PT',
    'romania': 'RO',
    'slovakia': 'SK',
    'slovenia': 'SI',
    'spain': 'ES',
    'sweden': 'SE',
    'united kingdom': 'GB',
    'uk': 'GB',
    'united states': 'US',
    'usa': 'US',
    'canada': 'CA',
    'australia': 'AU'
  };
  return map[name] || null;
}
